import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../../js/firebase';
import SigninForm from '../molecules/SigninForm';
import { signIn, getWorkspaces, getWorkspaceInvites, getUser } from '../../../actions';
import { FormattedMessage } from 'react-intl';
import AuthLayout from './AuthLayout';

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [ssoError, setSsoError] = useState('');

  const handleSsoSuccess = async () => {
    const user = auth.currentUser;
    const idToken = await user.getIdToken();
    dispatch(signIn(
      { token: idToken },
      async () => {
        await dispatch(getWorkspaces('SigninForm'));
        await dispatch(getWorkspaceInvites());
        await dispatch(getUser(localStorage.getItem('Id')));
        navigate('/select-workspace', { replace: true });
      },
      () => {},
      () => setSsoError('Authentication failed. Please try again.'),
    ));
  };

  const loginWithGoogle = async () => {
    setSsoError('');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      await handleSsoSuccess();
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') setSsoError('Google sign-in failed. Please try again.');
    }
  };

  const loginWithApple = async () => {
    setSsoError('');
    try {
      await signInWithPopup(auth, new OAuthProvider('apple.com'));
      await handleSsoSuccess();
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') setSsoError('Apple sign-in failed. Please try again.');
    }
  };

  return (
    <AuthLayout>
      <div className="mb-7 text-center">
        <h1 className="text-2xl font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.95)' }}>
          <FormattedMessage id="WELCOME_BACK_SIGN_IN" defaultMessage="Welcome back" />
        </h1>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign in to your Workflo account</p>
      </div>

      <div className="flex flex-col gap-2.5 mb-6">
        <button
          onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <img src="/images/icons/google-logo.svg" alt="" className="h-4 w-4" />
          <FormattedMessage id="CONTINUE_GOOGLE" defaultMessage="Continue with Google" />
        </button>
        <button
          onClick={loginWithApple}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <img src="/images/icons/apple-logo.svg" alt="" className="h-4 w-4" style={{ filter: 'brightness(0) invert(1)' }} />
          <FormattedMessage id="CONTINUE_APPLE" defaultMessage="Continue with Apple" />
        </button>
      </div>

      {ssoError && <p className="text-xs text-red-400 text-center -mt-2 mb-4">{ssoError}</p>}

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
        <span className="text-xs font-medium tracking-wide" style={{ color: 'rgba(255,255,255,0.3)' }}>OR</span>
        <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
      </div>

      <SigninForm dark />
    </AuthLayout>
  );
};

export default SignIn;
